import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";

import {
  apiClient,
  type ModelProvider,
  type ModelRoute,
  type ProviderAdapterDescriptor,
  type ProviderModel,
  type ProviderPolicy,
  type ProviderReadiness,
} from "../../api/client";
import { PasswordInput } from "../../components/PasswordInput";
import { errorMessage } from "./errors";

type Section = "connections" | "models" | "routes" | "governance";

export function ProviderManager() {
  const [section, setSection] = useState<Section>("connections");
  const [providers, setProviders] = useState<ModelProvider[]>([]);
  const [adapters, setAdapters] = useState<ProviderAdapterDescriptor[]>([]);
  const [models, setModels] = useState<ProviderModel[]>([]);
  const [routeModels, setRouteModels] = useState<ProviderModel[]>([]);
  const [routes, setRoutes] = useState<ModelRoute[]>([]);
  const [policies, setPolicies] = useState<ProviderPolicy[]>([]);
  const [readiness, setReadiness] = useState<ProviderReadiness | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const selected = providers.find((provider) => provider.id === selectedId) ?? providers[0];

  const loadProviders = useCallback(async () => {
    const values = await apiClient.listProviders();
    setProviders(values);
    setSelectedId((current) =>
      values.some((provider) => provider.id === current) ? current : (values[0]?.id ?? ""),
    );
    return values;
  }, []);

  const loadSelection = useCallback(async (providerId: string) => {
    if (!providerId) {
      setModels([]);
      setPolicies([]);
      setReadiness(null);
      return;
    }
    const [nextModels, nextPolicies, nextReadiness] = await Promise.all([
      apiClient.listProviderModels(providerId),
      apiClient.listProviderPolicies(providerId),
      apiClient.getProviderReadiness(providerId),
    ]);
    setModels(nextModels);
    setPolicies(nextPolicies);
    setReadiness(nextReadiness);
  }, []);

  const refresh = useCallback(async () => {
    const nextProviders = await loadProviders();
    const [nextRoutes, providerModels] = await Promise.all([
      apiClient.listModelRoutes(),
      Promise.all(nextProviders.map((provider) => apiClient.listProviderModels(provider.id))),
    ]);
    setRoutes(nextRoutes);
    setRouteModels(providerModels.flat());
  }, [loadProviders]);

  useEffect(() => {
    Promise.all([apiClient.listProviderAdapters(), refresh()])
      .then(([nextAdapters]) => setAdapters(nextAdapters))
      .catch((error: unknown) => setMessage(errorMessage(error)));
  }, [refresh]);

  useEffect(() => {
    void loadSelection(selected?.id ?? "").catch((error: unknown) =>
      setMessage(errorMessage(error)),
    );
  }, [loadSelection, selected?.id]);

  async function perform(action: () => Promise<string>) {
    setBusy(true);
    setMessage("");
    try {
      setMessage(await action());
      await refresh();
      await loadSelection(selected?.id ?? "");
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flow-panel provider-console" id="providers">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">owner · 模型网关</span>
          <h2>供应商与全局模型策略</h2>
          <p>先验证连接和模型定价，再发布治理策略、激活供应商，最后发布逻辑路由。</p>
        </div>
        <span className="phase-badge">{providers.length} 个供应商</span>
      </div>
      <nav aria-label="模型供应商管理" className="provider-console-tabs">
        {([
          ["connections", "连接"],
          ["models", "模型"],
          ["governance", "治理"],
          ["routes", "路由"],
        ] as const).map(([value, label]) => (
          <button
            aria-current={section === value ? "page" : undefined}
            className={section === value ? "active" : ""}
            key={value}
            onClick={() => setSection(value)}
            type="button"
          >
            {label}
          </button>
        ))}
      </nav>
      {message && <p className="status-banner" role="status">{message}</p>}
      {section === "connections" && (
        <ConnectionsSection
          adapters={adapters}
          busy={busy}
          onPerform={perform}
          onSelect={setSelectedId}
          providers={providers}
          readiness={readiness}
          selected={selected}
        />
      )}
      {section === "models" && selected && (
        <ModelsSection busy={busy} models={models} onPerform={perform} provider={selected} />
      )}
      {section === "routes" && (
        <RoutesSection busy={busy} models={routeModels} onPerform={perform} routes={routes} />
      )}
      {section === "governance" && selected && (
        <GovernanceSection
          busy={busy}
          onPerform={perform}
          policies={policies}
          provider={selected}
        />
      )}
      {section !== "connections" && !selected && (
        <p className="empty-page">请先在“连接”中添加供应商。</p>
      )}
    </section>
  );
}

function ConnectionsSection({
  adapters,
  providers,
  selected,
  readiness,
  busy,
  onSelect,
  onPerform,
}: {
  adapters: ProviderAdapterDescriptor[];
  providers: ModelProvider[];
  selected: ModelProvider | undefined;
  readiness: ProviderReadiness | null;
  busy: boolean;
  onSelect: (id: string) => void;
  onPerform: (action: () => Promise<string>) => Promise<void>;
}) {
  return (
    <div className="provider-console-grid">
      <ConnectionForm
        adapters={adapters}
        busy={busy}
        onCreate={async (payload) => {
          await onPerform(async () => {
            const provider = await apiClient.createProvider(payload);
            onSelect(provider.id);
            return `已保存 ${provider.display_name}，下一步请测试连接。`;
          });
        }}
      />
      <div className="provider-card-list" aria-label="供应商连接">
        {providers.map((provider) => (
          <button
            className={`provider-summary-card ${selected?.id === provider.id ? "selected" : ""}`}
            key={provider.id}
            onClick={() => onSelect(provider.id)}
            type="button"
          >
            <span>{provider.display_name}</span>
            <small>{connectionLabel(provider)} · {provider.enabled_model_count} 个已启用模型</small>
          </button>
        ))}
        {!providers.length && <p className="empty-page">尚无连接，请按左侧步骤添加。</p>}
        {selected && (
          <ConnectionDetail
            adapter={adapters.find((item) => item.code === selected.adapter_type)}
            busy={busy}
            onPerform={onPerform}
            provider={selected}
            readiness={readiness}
          />
        )}
      </div>
    </div>
  );
}

function ConnectionForm({
  adapters,
  busy,
  onCreate,
}: {
  adapters: ProviderAdapterDescriptor[];
  busy: boolean;
  onCreate: (payload: {
    code: string;
    display_name: string;
    adapter_type: string;
    endpoint_url: string | null;
    capabilities: string[];
    contract_metadata: Record<string, unknown>;
    adapter_options: Record<string, unknown>;
    credentials: Record<string, string> | null;
  }) => Promise<void>;
}) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [adapterCode, setAdapterCode] = useState("openai_compatible");
  const [endpoint, setEndpoint] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [region, setRegion] = useState("US");
  const adapter = adapters.find((item) => item.code === adapterCode) ?? adapters[0];

  async function submit(event: FormEvent) {
    event.preventDefault();
    await onCreate({
      code: code.trim(),
      display_name: name.trim(),
      adapter_type: adapter?.code ?? adapterCode,
      endpoint_url: endpoint.trim() || null,
      capabilities: adapter?.capabilities ?? ["text"],
      contract_metadata: { regions: [region.trim().toUpperCase()], retention_days: 0 },
      adapter_options: {},
      credentials: adapter?.credential_fields.length ? { api_key: apiKey } : null,
    });
    setApiKey("");
    setCode("");
    setName("");
  }

  return (
    <form className="admin-form connection-wizard" onSubmit={(event) => void submit(event)}>
      <span className="eyebrow">第 1 步</span>
      <h3>接入供应商</h3>
      <label>适配器<select value={adapter?.code ?? adapterCode} onChange={(event) => setAdapterCode(event.target.value)}>{adapters.map((item) => <option key={item.code} value={item.code}>{item.display_name}</option>)}</select></label>
      <p className="field-help">协议：{adapter?.protocol ?? "正在读取…"}</p>
      <label>代码<input required value={code} onChange={(event) => setCode(event.target.value)} /></label>
      <label>显示名称<input required value={name} onChange={(event) => setName(event.target.value)} /></label>
      {adapter?.endpoint_required && <label>API 基础 URL<input inputMode="url" placeholder="https://api.example.com/v1" required value={endpoint} onChange={(event) => setEndpoint(event.target.value)} /><small>必须为 HTTPS；不要填写 /chat/completions 或把 Key 放进 URL。</small></label>}
      <label>合同允许区域<input value={region} onChange={(event) => setRegion(event.target.value)} /></label>
      {!!adapter?.credential_fields.length && <div className="admin-form-field"><label htmlFor="provider-api-key">API Key（只写）</label><PasswordInput autoComplete="new-password" hideLabel="隐藏供应商 API Key" id="provider-api-key" onChange={(event) => setApiKey(event.target.value)} required showLabel="显示供应商 API Key" value={apiKey} /></div>}
      <button disabled={busy || !adapter} type="submit">保存连接</button>
    </form>
  );
}

function ConnectionDetail({
  provider,
  adapter,
  readiness,
  busy,
  onPerform,
}: {
  provider: ModelProvider;
  adapter: ProviderAdapterDescriptor | undefined;
  readiness: ProviderReadiness | null;
  busy: boolean;
  onPerform: (action: () => Promise<string>) => Promise<void>;
}) {
  const [credential, setCredential] = useState("");
  const [editing, setEditing] = useState(false);
  const [endpoint, setEndpoint] = useState(provider.endpoint_url ?? "");
  const [adapterOptions, setAdapterOptions] = useState(
    JSON.stringify(provider.adapter_options, null, 2),
  );
  useEffect(() => {
    setEditing(false);
    setEndpoint(provider.endpoint_url ?? "");
    setAdapterOptions(JSON.stringify(provider.adapter_options, null, 2));
  }, [provider.id, provider.endpoint_url, provider.adapter_options]);
  return (
    <article className="connection-detail-card">
      <div className="panel-heading"><div><span className="eyebrow">当前连接</span><h3>{provider.display_name}</h3></div><span className="phase-badge">{connectionLabel(provider)}</span></div>
      <dl className="admin-facts"><div><dt>端点</dt><dd>{provider.endpoint_url ?? "无需端点"}</dd></div><div><dt>连接版本</dt><dd>v{provider.connection_config_version}</dd></div><div><dt>凭证</dt><dd>{provider.credential_configured ? `已配置 · v${provider.credential_version}` : "未配置"}</dd></div></dl>
      {readiness && <ul className="readiness-list">{[["适配器", readiness.adapter_registered], ["凭证", readiness.credentials_ready], ["最新版本验证", readiness.latest_version_verified], ["已启用模型", readiness.enabled_model_count > 0], ["活动治理策略", readiness.active_policy]].map(([label, passed]) => <li className={passed ? "ready" : "missing"} key={String(label)}>{passed ? "✓" : "○"} {label}</li>)}</ul>}
      <div className="admin-inline-actions"><button disabled={busy} onClick={() => void onPerform(async () => { const result = await apiClient.verifyProviderConnection(provider.id); return result.result_code === "verified" ? `连接测试成功，发现 ${result.discovered_model_count} 个模型。` : `连接测试失败：${result.result_code}`; })} type="button">测试连接</button><button disabled={busy || !adapter} onClick={() => setEditing((value) => !value)} type="button">编辑连接</button><button disabled={busy || !readiness?.ready || provider.state === "active"} onClick={() => void onPerform(async () => { await apiClient.setProviderState(provider.id, "activate"); return "供应商已激活。"; })} type="button">激活</button><button className="danger-outline-button" disabled={busy || provider.state === "disabled"} onClick={() => void onPerform(async () => { await apiClient.setProviderState(provider.id, "disable"); return "供应商已停用。"; })} type="button">停用</button></div>
      {editing && adapter && <form className="admin-form compact-admin-form" onSubmit={(event) => { event.preventDefault(); void onPerform(async () => { await apiClient.updateProviderConnection(provider.id, { endpoint_url: adapter.endpoint_supported ? endpoint.trim() || null : null, adapter_options: jsonObject(adapterOptions) }); setEditing(false); return "连接已更新，状态已变更；请重新测试连接。"; }); }}><h4>编辑连接配置</h4>{adapter.endpoint_supported && <label>API 基础 URL<input inputMode="url" required={adapter.endpoint_required} value={endpoint} onChange={(event) => setEndpoint(event.target.value)} /><small>仅允许 HTTPS 基础 URL；不得包含 API Key。</small></label>}{adapter.option_fields.length > 0 && <label>适配器选项 JSON<textarea rows={4} value={adapterOptions} onChange={(event) => setAdapterOptions(event.target.value)} /><small>只填写适配器声明的非密钥选项。</small></label>}<button disabled={busy} type="submit">保存连接变更</button></form>}
      {provider.credential_configured && <div className="admin-inline-actions"><PasswordInput aria-label="轮换后的 API Key" autoComplete="new-password" hideLabel="隐藏轮换后的 API Key" onChange={(event) => setCredential(event.target.value)} placeholder="新 API Key" showLabel="显示轮换后的 API Key" value={credential} /><button disabled={busy || !credential} onClick={() => void onPerform(async () => { await apiClient.rotateProviderCredentials(provider.id, { api_key: credential }); setCredential(""); return "凭证已轮换；连接需要重新测试。"; })} type="button">轮换凭证</button></div>}
    </article>
  );
}

function ModelsSection({ provider, models, busy, onPerform }: { provider: ModelProvider; models: ProviderModel[]; busy: boolean; onPerform: (action: () => Promise<string>) => Promise<void> }) {
  const [modelId, setModelId] = useState("");
  const [capability, setCapability] = useState("text");
  const [contextWindow, setContextWindow] = useState("");
  const [maxOutput, setMaxOutput] = useState("");
  const [estimatedCost, setEstimatedCost] = useState("0");
  const [inputPrice, setInputPrice] = useState("0");
  const [outputPrice, setOutputPrice] = useState("0");
  const [cachedPrice, setCachedPrice] = useState("0");
  const [imagePrice, setImagePrice] = useState("0");
  async function register(event: FormEvent) {
    event.preventDefault();
    await onPerform(async () => {
      await apiClient.registerProviderModel(provider.id, {
        model_id: modelId.trim(),
        display_name: modelId.trim(),
        capabilities: [capability],
        context_window: optionalNumber(contextWindow),
        max_output_tokens: optionalNumber(maxOutput),
        pricing_metadata: pricingMetadata(
          [capability], estimatedCost, inputPrice, outputPrice, cachedPrice, imagePrice,
        ),
        pricing_version: "manual-v1",
      });
      setModelId("");
      return "模型已登记；验证并启用后才能进入路由。";
    });
  }
  return (
    <div className="provider-section-stack">
      <div className="panel-heading">
        <div><span className="eyebrow">第 2 步</span><h3>模型目录 · {provider.display_name}</h3></div>
        <button disabled={busy || provider.verification_state !== "verified"} onClick={() => void onPerform(async () => { const values = await apiClient.discoverProviderModels(provider.id); return `已刷新 ${values.length} 个候选，未自动启用。`; })} type="button">发现模型</button>
      </div>
      <div className="provider-model-grid">
        {models.map((model) => <ModelCard busy={busy} key={model.id} model={model} onPerform={onPerform} />)}
      </div>
      <form className="admin-form compact-admin-form" onSubmit={register}>
        <h4>手工登记模型</h4>
        <label>供应商模型 ID<input required value={modelId} onChange={(event) => setModelId(event.target.value)} /></label>
        <label>能力<select value={capability} onChange={(event) => setCapability(event.target.value)}><option value="text">文本</option><option value="vision">视觉理解</option><option value="image">图片生成</option></select></label>
        <label>上下文上限（可选）<input min="1" type="number" value={contextWindow} onChange={(event) => setContextWindow(event.target.value)} /></label>
        <label>输出上限（可选）<input min="1" type="number" value={maxOutput} onChange={(event) => setMaxOutput(event.target.value)} /></label>
        <label>单次预计成本<input min="0" step="0.000001" type="number" value={estimatedCost} onChange={(event) => setEstimatedCost(event.target.value)} /></label>
        {capability === "image" ? (
          <label>每张图片单价<input min="0" step="0.000001" type="number" value={imagePrice} onChange={(event) => setImagePrice(event.target.value)} /></label>
        ) : (
          <>
            <label>每千输入 Token 单价<input min="0" step="0.000001" type="number" value={inputPrice} onChange={(event) => setInputPrice(event.target.value)} /></label>
            <label>每千输出 Token 单价<input min="0" step="0.000001" type="number" value={outputPrice} onChange={(event) => setOutputPrice(event.target.value)} /></label>
            <label>每千缓存 Token 单价<input min="0" step="0.000001" type="number" value={cachedPrice} onChange={(event) => setCachedPrice(event.target.value)} /></label>
          </>
        )}
        <button disabled={busy} type="submit">登记为禁用候选</button>
      </form>
    </div>
  );
}

function ModelCard({ model, busy, onPerform }: { model: ProviderModel; busy: boolean; onPerform: (action: () => Promise<string>) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [history, setHistory] = useState<Array<Record<string, unknown>> | null>(null);
  const [contextWindow, setContextWindow] = useState(String(model.context_window ?? ""));
  const [maxOutput, setMaxOutput] = useState(String(model.max_output_tokens ?? ""));
  const [estimatedCost, setEstimatedCost] = useState(String(model.pricing_metadata.estimated_cost ?? "0"));
  const [inputPrice, setInputPrice] = useState(String(model.pricing_metadata.input_per_thousand ?? "0"));
  const [outputPrice, setOutputPrice] = useState(String(model.pricing_metadata.output_per_thousand ?? "0"));
  const [cachedPrice, setCachedPrice] = useState(String(model.pricing_metadata.cached_per_thousand ?? "0"));
  const [imagePrice, setImagePrice] = useState(String(model.pricing_metadata.image_per_unit ?? "0"));
  const [pricingVersion, setPricingVersion] = useState(model.pricing_version);
  return (
    <article className="provider-model-card">
      <div><span className="task-type">{model.source}</span><h4>{model.display_name}</h4><p>{model.model_id} · {model.capabilities.join(" / ")} · 修订 {model.revision}</p><p>上下文 {model.context_window ?? "未声明"} · 输出 {model.max_output_tokens ?? "未声明"} · 定价 {model.pricing_version}</p></div>
      <div className="admin-inline-actions">
        <button disabled={busy || model.validated} onClick={() => void onPerform(async () => { await apiClient.setProviderModelState(model.id, "validate"); return "模型已验证。"; })} type="button">验证</button>
        <button disabled={busy || !model.validated} onClick={() => void onPerform(async () => { await apiClient.setProviderModelState(model.id, model.state === "enabled" ? "disable" : "enable"); return model.state === "enabled" ? "模型已停用。" : "模型已启用。"; })} type="button">{model.state === "enabled" ? "停用" : "启用"}</button>
        <button disabled={busy || model.state === "enabled"} onClick={() => setEditing((value) => !value)} type="button">修改限制与定价</button>
        <button disabled={busy} onClick={() => void apiClient.listProviderModelRevisions(model.id).then(setHistory)} type="button">查看修订</button>
      </div>
      {editing && <form className="admin-form compact-admin-form" onSubmit={(event) => { event.preventDefault(); void onPerform(async () => { await apiClient.updateProviderModel(model.id, { model_id: model.model_id, display_name: model.display_name, capabilities: model.capabilities, context_window: optionalNumber(contextWindow), max_output_tokens: optionalNumber(maxOutput), pricing_metadata: pricingMetadata(model.capabilities, estimatedCost, inputPrice, outputPrice, cachedPrice, imagePrice), pricing_version: pricingVersion.trim() }); setEditing(false); return "模型配置已形成新修订，需要重新验证。"; }); }}><label>上下文上限<input min="1" type="number" value={contextWindow} onChange={(event) => setContextWindow(event.target.value)} /></label><label>输出上限<input min="1" type="number" value={maxOutput} onChange={(event) => setMaxOutput(event.target.value)} /></label><label>单次预计成本<input min="0" step="0.000001" type="number" value={estimatedCost} onChange={(event) => setEstimatedCost(event.target.value)} /></label>{model.capabilities.some((item) => item === "text" || item === "vision") && <><label>每千输入 Token 单价<input min="0" step="0.000001" type="number" value={inputPrice} onChange={(event) => setInputPrice(event.target.value)} /></label><label>每千输出 Token 单价<input min="0" step="0.000001" type="number" value={outputPrice} onChange={(event) => setOutputPrice(event.target.value)} /></label><label>每千缓存 Token 单价<input min="0" step="0.000001" type="number" value={cachedPrice} onChange={(event) => setCachedPrice(event.target.value)} /></label></>}{model.capabilities.includes("image") && <label>每张图片单价<input min="0" step="0.000001" type="number" value={imagePrice} onChange={(event) => setImagePrice(event.target.value)} /></label>}<label>定价版本<input required value={pricingVersion} onChange={(event) => setPricingVersion(event.target.value)} /></label><button disabled={busy} type="submit">保存新修订</button></form>}
      {history && <details open><summary>不可变修订历史（{history.length}）</summary><ol>{history.map((item, index) => <li key={String(item.revision ?? index)}>修订 {String(item.revision ?? "-")} · {String(item.state ?? "-")} · {String(item.pricing_version ?? "-")}</li>)}</ol></details>}
    </article>
  );
}

function RoutesSection({ routes, models, busy, onPerform }: { routes: ModelRoute[]; models: ProviderModel[]; busy: boolean; onPerform: (action: () => Promise<string>) => Promise<void> }) {
  const enabled = models.filter((model) => model.state === "enabled");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [capability, setCapability] = useState("text");
  const [candidateIds, setCandidateIds] = useState<string[]>([]);
  async function create(event: FormEvent) {
    event.preventDefault();
    await onPerform(async () => {
      await apiClient.createModelRoute({ code: code.trim(), display_name: name.trim(), capability, description: "", candidate_ids: candidateIds });
      setCode(""); setName(""); setCandidateIds([]);
      return "路由草稿已创建，请确认顺序和资格预览后发布。";
    });
  }
  const eligible = enabled.filter((model) => model.capabilities.includes(capability));
  return (
    <div className="provider-section-stack">
      <div><span className="eyebrow">第 4 步</span><h3>逻辑模型路由</h3><p>先完成治理策略并激活供应商；候选严格按下列顺序回退。</p></div>
      <div className="route-card-grid">{routes.map((route) => <RouteCard busy={busy} eligible={enabled.filter((model) => model.capabilities.includes(route.capability))} key={route.id} onPerform={onPerform} route={route} />)}</div>
      <form className="admin-form" onSubmit={create}><h4>创建路由草稿</h4><label>路由代码<input required value={code} onChange={(event) => setCode(event.target.value)} placeholder="general-text" /></label><label>显示名称<input required value={name} onChange={(event) => setName(event.target.value)} /></label><label>能力<select value={capability} onChange={(event) => { setCapability(event.target.value); setCandidateIds([]); }}><option value="text">文本</option><option value="vision">视觉理解</option><option value="image">图片生成</option></select></label><CandidateOrder candidateIds={candidateIds} models={eligible} onChange={setCandidateIds} /><button disabled={busy || !candidateIds.length} type="submit">创建草稿</button></form>
    </div>
  );
}

function RouteCard({ route, eligible, busy, onPerform }: { route: ModelRoute; eligible: ProviderModel[]; busy: boolean; onPerform: (action: () => Promise<string>) => Promise<void> }) {
  const active = route.versions.find((version) => version.state === "active");
  const draft = route.versions.find((version) => version.state === "draft");
  const [candidateIds, setCandidateIds] = useState<string[]>([]);
  return <article className="route-card"><span className="task-type">{route.capability}</span><h4>{route.display_name}</h4><p>{route.code} · {active ? `活动 v${active.version}` : "尚未发布"}</p>{draft && <ol className="route-candidate-list">{draft.candidates.map((candidate) => <li key={candidate.id}>{candidate.position + 1}. {candidate.provider_code} / {candidate.model_id}</li>)}</ol>}{draft && draft.diagnostics.length > 0 && <ul className="readiness-list">{draft.diagnostics.map((item) => <li className="missing" key={item}>○ {item}</li>)}</ul>}<div className="admin-inline-actions">{draft && <button disabled={busy || draft.diagnostics.length > 0} onClick={() => { if (window.confirm(`确认发布 ${route.display_name} v${draft.version}？发布后候选顺序不可修改。`)) void onPerform(async () => { await apiClient.setModelRouteVersionState(draft.id, "publish"); return `${route.display_name} 已发布。`; }); }} type="button">发布 v{draft.version}</button>}{active && <button className="text-button" disabled={busy} onClick={() => void onPerform(async () => { await apiClient.setModelRouteVersionState(active.id, "disable"); return `${route.display_name} 已停用。`; })} type="button">停用活动版本</button>}</div><details><summary>创建下一版候选</summary><CandidateOrder candidateIds={candidateIds} models={eligible} onChange={setCandidateIds} /><button disabled={busy || !candidateIds.length} onClick={() => void onPerform(async () => { const version = await apiClient.createModelRouteVersion(route.id, candidateIds); setCandidateIds([]); return `已创建草稿 v${version.version}。`; })} type="button">保存新版本</button></details></article>;
}

function CandidateOrder({ models, candidateIds, onChange }: { models: ProviderModel[]; candidateIds: string[]; onChange: (ids: string[]) => void }) {
  const selected = candidateIds.map((id) => models.find((model) => model.id === id)).filter((model): model is ProviderModel => Boolean(model));
  return <fieldset><legend>主模型与回退顺序</legend><p className="field-help">这里只显示已启用且能力匹配的模型；发布时服务端会再次检查连接和治理策略。</p><ol className="route-candidate-list">{selected.map((model, index) => <li key={model.id}>{index + 1}. {model.display_name}<button aria-label={`上移 ${model.display_name}`} disabled={index === 0} onClick={() => onChange(moveItem(candidateIds, index, index - 1))} type="button">↑</button><button aria-label={`下移 ${model.display_name}`} disabled={index === selected.length - 1} onClick={() => onChange(moveItem(candidateIds, index, index + 1))} type="button">↓</button><button onClick={() => onChange(candidateIds.filter((id) => id !== model.id))} type="button">移除</button></li>)}</ol>{models.filter((model) => !candidateIds.includes(model.id)).map((model) => <button className="text-button" key={model.id} onClick={() => onChange([...candidateIds, model.id])} type="button">添加 {model.display_name}</button>)}{!models.length && <p>暂无满足能力且已启用的模型。</p>}</fieldset>;
}

function GovernanceSection({ provider, policies, busy, onPerform }: { provider: ModelProvider; policies: ProviderPolicy[]; busy: boolean; onPerform: (action: () => Promise<string>) => Promise<void> }) {
  const [classifications, setClassifications] = useState<string[]>(["internal", "confidential"]);
  const [regions, setRegions] = useState("US");
  const [maxCost, setMaxCost] = useState("5.00");
  const [retention, setRetention] = useState("0");
  const [advanced, setAdvanced] = useState("{}");
  const capabilities = useMemo(() => provider.capabilities, [provider.capabilities]);
  async function create(event: FormEvent) { event.preventDefault(); await onPerform(async () => { const extras = jsonObject(advanced); const policy = await apiClient.createProviderPolicy(provider.id, { allowed_classifications: classifications as ProviderPolicy["allowed_classifications"], allowed_capabilities: capabilities, allowed_regions: commaList(regions), max_cost_per_run: maxCost, policy_document: { ...extras, contract_approved: true, retention_days: Number(retention) } }); return `治理策略 v${policy.version} 已创建。`; }); }
  return <div className="provider-section-stack"><div><span className="eyebrow">第 3 步</span><h3>治理策略 · {provider.display_name}</h3><p>发布策略后回到“连接”激活供应商，再进入“路由”发布版本。</p></div><div className="provider-policy-list">{policies.map((policy) => <div key={policy.id}><span>v{policy.version} · {policy.state} · 单次上限 {policy.max_cost_per_run}</span><button className="text-button" disabled={busy || policy.state !== "draft"} onClick={() => void onPerform(async () => { await apiClient.publishProviderPolicy(policy.id); return `策略 v${policy.version} 已发布。`; })} type="button">发布</button></div>)}</div><form className="admin-form governance-form" onSubmit={create}><fieldset><legend>允许数据分级</legend>{["internal", "confidential", "restricted"].map((value) => <label className="checkbox-row" key={value}><input checked={classifications.includes(value)} onChange={(event) => setClassifications((current) => event.target.checked ? [...current, value] : current.filter((item) => item !== value))} type="checkbox" />{value}</label>)}</fieldset><label>允许区域<input value={regions} onChange={(event) => setRegions(event.target.value)} /></label><label>数据保留天数<input min="0" type="number" value={retention} onChange={(event) => setRetention(event.target.value)} /></label><label>单次运行成本上限<input min="0" step="0.01" type="number" value={maxCost} onChange={(event) => setMaxCost(event.target.value)} /></label><details><summary>高级策略元数据</summary><label>附加 JSON<textarea rows={4} value={advanced} onChange={(event) => setAdvanced(event.target.value)} /></label></details><button disabled={busy || !classifications.length} type="submit">创建下一版策略</button></form></div>;
}

function connectionLabel(provider: ModelProvider): string {
  if (provider.state === "active") return "已激活";
  if (provider.verification_state === "verified") return "连接已验证";
  if (provider.verification_state === "failed") return "测试失败";
  if (provider.verification_state === "stale") return "配置已变更";
  return "尚未测试";
}

function commaList(value: string): string[] {
  return value.split(",").map((item) => item.trim()).filter(Boolean);
}

function optionalNumber(value: string): number | null {
  return value.trim() ? Number(value) : null;
}

function pricingMetadata(
  capabilities: string[],
  estimatedCost: string,
  inputPrice: string,
  outputPrice: string,
  cachedPrice: string,
  imagePrice: string,
): Record<string, string> {
  const pricing: Record<string, string> = { estimated_cost: estimatedCost };
  if (capabilities.some((item) => item === "text" || item === "vision")) {
    pricing.input_per_thousand = inputPrice;
    pricing.output_per_thousand = outputPrice;
    pricing.cached_per_thousand = cachedPrice;
  }
  if (capabilities.includes("image")) pricing.image_per_unit = imagePrice;
  return pricing;
}

function moveItem(values: string[], from: number, to: number): string[] {
  const next = [...values];
  const [item] = next.splice(from, 1);
  if (item !== undefined) next.splice(to, 0, item);
  return next;
}

function jsonObject(value: string): Record<string, unknown> {
  const parsed: unknown = JSON.parse(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("必须填写 JSON 对象。");
  return parsed as Record<string, unknown>;
}
